// API Client for CarP2P backend

// Auto-detect: use proxy in dev, full worker URL in production
const API_BASE = window.location.hostname === 'localhost'
  ? '/api'
  : 'https://purplemine.mkim0818.workers.dev/api';

interface ApiOptions {
  method?: string;
  body?: any;
  headers?: Record<string, string>;
  isFormData?: boolean;
}

class ApiError extends Error {
  status: number;
  data: any;

  constructor(status: number, data: any) {
    super(data?.error || 'API Error');
    this.status = status;
    this.data = data;
  }
}

async function request<T = any>(path: string, options: ApiOptions = {}): Promise<T> {
  const token = localStorage.getItem('carp2p_token');

  const headers: Record<string, string> = {
    ...(options.isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    method: options.method || 'GET',
    headers,
    body: options.isFormData ? options.body : (options.body ? JSON.stringify(options.body) : undefined),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: 'Unknown error' }));
    if (res.status === 401) {
      localStorage.removeItem('carp2p_token');
      localStorage.removeItem('carp2p_user');
      window.location.href = '/signin';
    }
    throw new ApiError(res.status, data);
  }

  return res.json();
}

// --- Auth ---
export const authApi = {
  signUp: (data: any) => request('/auth/signup', { method: 'POST', body: data }),
  signIn: (data: any) => request('/auth/signin', { method: 'POST', body: data }),
  me: () => request('/auth/me'),
  uploadVerification: (formData: FormData) =>
    request('/auth/verify', { method: 'POST', body: formData, isFormData: true }),
  addRole: (role: string) => request('/auth/add-role', { method: 'POST', body: { role } }),
};

// --- Vehicles ---
export const vehicleApi = {
  search: (filters: Record<string, any>) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== '') params.set(k, String(v));
    });
    return request(`/vehicles?${params.toString()}`);
  },
  getById: (id: string) => request(`/vehicles/${id}`),
  update: (id: string, data: any) => request(`/vehicles/${id}`, { method: 'PUT', body: data }),
  hostVehicles: () => request('/vehicles/host/vehicles'),
  getRentals: (id: string) => request(`/vehicles/${id}/rentals`),
  getAvailability: (id: string, year: number, month: number) =>
    request(`/vehicles/${id}/availability?year=${year}&month=${month}`),
  uploadPhoto: (id: string, formData: FormData) =>
    request(`/vehicles/${id}/photos`, { method: 'POST', body: formData, isFormData: true }),
  deletePhoto: (vehicleId: string, photoId: string) =>
    request(`/vehicles/${vehicleId}/photos/${photoId}`, { method: 'DELETE' }),
};

// --- Reservations ---
export const reservationApi = {
  create: (data: any) => request('/reservations', { method: 'POST', body: data }),
  list: (params?: Record<string, any>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/reservations${qs}`);
  },
  getById: (id: string) => request(`/reservations/${id}`),
  transition: (id: string, event: string, notes?: string) =>
    request(`/reservations/${id}/transition`, { method: 'POST', body: { event, notes } }),
  registerCard: (id: string, data: { authKey: string; customerKey: string }) =>
    request(`/reservations/${id}/payment/register-card`, { method: 'POST', body: data }),
  chargePayment: (id: string, data: { amount?: number; orderName?: string }) =>
    request(`/reservations/${id}/payment/charge`, { method: 'POST', body: data }),
  cancelPayment: (id: string, data: { reason: string; cancelAmount?: number }) =>
    request(`/reservations/${id}/payment/cancel`, { method: 'POST', body: data }),
  uploadInspectionPhoto: (id: string, formData: FormData) =>
    request(`/reservations/${id}/inspection-photos`, { method: 'POST', body: formData, isFormData: true }),
};

// --- Admin ---
export const adminApi = {
  dashboard: () => request('/admin/dashboard'),
  analytics: () => request('/admin/analytics'),
  feeRates: () => request('/admin/config/fee-rates'),
  configPolicies: () => request('/admin/config/policies'),
  rejectionReasons: () => request('/admin/config/rejection-reasons'),
  createRejectionReason: (data: any) => request('/admin/config/rejection-reasons', { method: 'POST', body: data }),
  vehiclesAll: (params?: any) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/admin/vehicles/all${qs}`);
  },
  pendingVehicles: (page?: number) => request(`/admin/vehicles/pending?page=${page || 1}`),
  approveVehicle: (id: string, notes?: string) =>
    request(`/admin/vehicles/${id}/approve`, { method: 'POST', body: { notes } }),
  rejectVehicle: (id: string, reason: string) =>
    request(`/admin/vehicles/${id}/reject`, { method: 'POST', body: { reason } }),
  reservations: (params?: any) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/admin/reservations${qs}`);
  },
  hostApproveReservation: (id: string, notes?: string) =>
    request(`/admin/reservations/${id}/host-approve`, { method: 'POST', body: { notes } }),
  approveReservation: (id: string, data: { notes?: string; total_amount?: number }) =>
    request(`/admin/reservations/${id}/approve`, { method: 'POST', body: data }),
  rejectReservation: (id: string, reason: string) =>
    request(`/admin/reservations/${id}/reject`, { method: 'POST', body: { reason } }),
  runSettlement: (periodStart: string, periodEnd: string) =>
    request('/admin/settlements/run', { method: 'POST', body: { period_start: periodStart, period_end: periodEnd } }),
  settlements: () => request('/admin/settlements'),
  policies: () => request('/admin/config/policies'),
  updatePolicy: (id: string, value: any) =>
    request(`/admin/config/policies/${id}`, { method: 'PUT', body: { policy_value: value } }),
};

// --- Services ---
export const serviceApi = {
  list: () => request('/services'),
};

// --- Settlements ---
export const settlementApi = {
  list: (params?: any) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/settlements${qs}`);
  },
  total: () => request('/settlements/total'),
};

// --- Carriers ---
export const carrierApi = {
  getSteps: (reservationId: string) => request(`/carriers/reservation/${reservationId}`),
  updateStep: (stepId: string, data: any) =>
    request(`/carriers/steps/${stepId}`, { method: 'PUT', body: data }),
  uploadPhoto: (stepId: string, formData: FormData) =>
    request(`/carriers/steps/${stepId}/photos`, { method: 'POST', body: formData, isFormData: true }),
};

export { ApiError };
